'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CollaborationPortalProps {
  onSubmit: (data: CollaborationFormData) => void;
}

interface CollaborationFormData {
  name: string;
  email: string;
  institution: string;
  role: string;
  researchArea: string;
  proposalType: string;
  description: string;
}

const CollaborationPortal: React.FC<CollaborationPortalProps> = ({ onSubmit }) => {
  const [formData, setFormData] = useState<CollaborationFormData>({
    name: '',
    email: '',
    institution: '',
    role: '',
    researchArea: '',
    proposalType: '',
    description: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <section className="py-12 bg-muted/30">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-headline font-bold text-primary mb-4">
            Research Collaboration Portal
          </h2>
          <p className="text-base font-body text-foreground/80">
            Partner with us to advance the science of forgiveness and peace-building
          </p>
        </div>

        <div className="bg-card rounded-lg shadow-md p-8 border border-border">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-body font-medium text-foreground mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-input rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Dr. Jane Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-medium text-foreground mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-input rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="jane.smith@university.edu"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-body font-medium text-foreground mb-2">
                  Institution *
                </label>
                <input
                  type="text"
                  name="institution"
                  value={formData.institution}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-input rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="University of Peace Studies"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-medium text-foreground mb-2">
                  Role *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-input rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Select your role</option>
                  <option value="professor">Professor</option>
                  <option value="researcher">Researcher</option>
                  <option value="phd">PhD Student</option>
                  <option value="postdoc">Postdoctoral Fellow</option>
                  <option value="practitioner">Practitioner</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-body font-medium text-foreground mb-2">
                  Research Area *
                </label>
                <select
                  name="researchArea"
                  value={formData.researchArea}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-input rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Select research area</option>
                  <option value="forgiveness">Forgiveness Psychology</option>
                  <option value="peace">Peace Building</option>
                  <option value="conflict">Conflict Resolution</option>
                  <option value="trauma">Trauma Healing</option>
                  <option value="community">Community Impact</option>
                  <option value="policy">Policy Research</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-body font-medium text-foreground mb-2">
                  Proposal Type *
                </label>
                <select
                  name="proposalType"
                  value={formData.proposalType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-input rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Select proposal type</option>
                  <option value="joint-research">Joint Research Project</option>
                  <option value="data-sharing">Data Sharing Partnership</option>
                  <option value="methodology">Methodology Consultation</option>
                  <option value="publication">Co-Publication Opportunity</option>
                  <option value="grant">Grant Collaboration</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-body font-medium text-foreground mb-2">
                Research Proposal Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={6}
                className="w-full px-4 py-2 border border-input rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                placeholder="Describe your research proposal, objectives, methodology, and how collaboration with SIG Day would benefit both parties..."
              />
            </div>

            <div className="flex items-center justify-center">
              <button
                type="submit"
                className="px-8 py-3 bg-accent text-accent-foreground rounded-md font-cta text-base font-bold transition-all duration-300 hover:scale-105 hover:shadow-md flex items-center space-x-2"
              >
                <Icon name="PaperAirplaneIcon" size={20} />
                <span>Submit Collaboration Proposal</span>
              </button>
            </div>
          </form>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-card rounded-lg p-6 shadow-md border border-border text-center">
            <Icon name="AcademicCapIcon" size={32} className="text-primary mx-auto mb-3" />
            <h3 className="text-lg font-headline font-bold text-primary mb-2">
              Academic Partnerships
            </h3>
            <p className="text-sm font-body text-foreground/80">
              Collaborate on peer-reviewed research and publications
            </p>
          </div>

          <div className="bg-card rounded-lg p-6 shadow-md border border-border text-center">
            <Icon name="ChartBarIcon" size={32} className="text-primary mx-auto mb-3" />
            <h3 className="text-lg font-headline font-bold text-primary mb-2">Data Access</h3>
            <p className="text-sm font-body text-foreground/80">
              Access our global dataset for approved research projects
            </p>
          </div>

          <div className="bg-card rounded-lg p-6 shadow-md border border-border text-center">
            <Icon name="GlobeAltIcon" size={32} className="text-primary mx-auto mb-3" />
            <h3 className="text-lg font-headline font-bold text-primary mb-2">
              Global Network
            </h3>
            <p className="text-sm font-body text-foreground/80">
              Connect with researchers across 50+ countries
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CollaborationPortal;